import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Assignment,
  TurnAssignmentCandidateResource,
  TurnAssignmentResource,
  turnsAssign,
} from '~/generated';
import { extractData } from '~/utils/query-utils';

export const useToggleTurnAssignment = (
  options: UseMutationOptions<
    TurnAssignmentResource[],
    unknown,
    {
      id: number;
      shiftApplicationId: number;
    },
    { previousCandidates: TurnAssignmentCandidateResource[] | undefined }
  > = {}
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    scope: { id: 'turnAssignment' },
    mutationFn: async (args: {
      id: number;
      shiftApplicationId: number;
      shiftId: number;
    }) => {
      const assignments = queryClient
        .getQueryData<TurnAssignmentCandidateResource[]>([
          'turns',
          args.id,
          'candidates',
        ])
        ?.filter((candidate) => candidate.assignment)
        .flatMap((candidate) => ({
          shiftApplicationId: candidate.assignment?.shiftApplicationId,
          isLead: candidate.assignment?.isLead,
        })) as Assignment[];

      return extractData(
        turnsAssign({
          path: { id: args.id },
          body: assignments,
        })
      );
    },
    async onMutate(variables) {
      await queryClient.cancelQueries({
        queryKey: ['turns', variables.id, 'candidates'],
      });

      const previousCandidates = queryClient.getQueryData([
        'turns',
        variables.id,
        'candidates',
      ]) as TurnAssignmentCandidateResource[] | undefined;

      queryClient.setQueryData<TurnAssignmentCandidateResource[]>(
        ['turns', variables.id, 'candidates'],
        (candidates) =>
          candidates?.map((candidate) => {
            if (candidate.application?.id === variables.shiftApplicationId) {
              return {
                ...candidate,

                assignmentRatioNumerator: candidate.assignment
                  ? (candidate.assignmentRatioNumerator ?? 0) - 1
                  : (candidate.assignmentRatioNumerator ?? 0) + 1,
                assignment: candidate.assignment
                  ? null
                  : {
                      shiftApplicationId: variables.shiftApplicationId,
                      isLead: false,
                    },
              };
            }
            return candidate;
          })
      );

      return { previousCandidates };
    },
    onError(err, variables, context) {
      queryClient.setQueryData(
        ['turns', variables.id, 'candidates'],
        context?.previousCandidates
      );
    },
    async onSettled(data, error, variables) {
      queryClient.invalidateQueries({
        queryKey: ['turns', variables.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['shifts', variables.shiftId],
      });
    },
  });
};

export const useRemoveAllTurnAssignments = (
  options: UseMutationOptions<
    TurnAssignmentResource[],
    unknown,
    {
      id: number;
    },
    { previousCandidates: TurnAssignmentCandidateResource[] | undefined }
  > = {}
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    scope: { id: 'turnAssignment' },
    mutationFn: async (args: { id: number; shiftId: number }) =>
      extractData(
        turnsAssign({
          path: { id: args.id },
          body: [],
        })
      ),
    async onMutate(variables) {
      await queryClient.cancelQueries({
        queryKey: ['turns', variables.id, 'candidates'],
      });

      const previousCandidates = queryClient.getQueryData([
        'turns',
        variables.id,
        'candidates',
      ]) as TurnAssignmentCandidateResource[] | undefined;

      queryClient.setQueryData<TurnAssignmentCandidateResource[]>(
        ['turns', variables.id, 'candidates'],
        (candidates) =>
          candidates?.map((candidate) => ({
            ...candidate,
            assignment: null,
          }))
      );

      return { previousCandidates };
    },
    onError(err, variables, context) {
      queryClient.setQueryData(
        ['turns', variables.id, 'candidates'],
        context?.previousCandidates
      );
    },
    async onSettled(data, error, variables) {
      queryClient.invalidateQueries({
        queryKey: ['turns', variables.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['shifts', variables.shiftId],
      });
    },
  });
};

export const useAssignPublisherAsLead = (
  options: UseMutationOptions<
    TurnAssignmentResource[],
    unknown,
    {
      id: number;
      shiftApplicationId: number;
    },
    { previousCandidates: TurnAssignmentCandidateResource[] | undefined }
  > = {}
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    scope: { id: 'turnAssignment' },
    mutationFn: async (args: { id: number; shiftApplicationId: number }) => {
      const assignments = queryClient
        .getQueryData<TurnAssignmentCandidateResource[]>([
          'turns',
          args.id,
          'candidates',
        ])
        ?.filter((candidate) => candidate.assignment)
        .flatMap((candidate) => ({
          shiftApplicationId: candidate.assignment?.shiftApplicationId,
          isLead: candidate.assignment?.isLead,
        })) as Assignment[];

      return extractData(
        turnsAssign({
          path: { id: args.id },
          body: assignments,
        })
      );
    },
    async onMutate(variables) {
      await queryClient.cancelQueries({
        queryKey: ['turns', variables.id, 'candidates'],
      });

      const previousCandidates = queryClient.getQueryData([
        'turns',
        variables.id,
        'candidates',
      ]) as TurnAssignmentCandidateResource[] | undefined;

      queryClient.setQueryData<TurnAssignmentCandidateResource[]>(
        ['turns', variables.id, 'candidates'],
        (candidates) =>
          candidates?.map((candidate) => {
            if (candidate.application?.id === variables.shiftApplicationId) {
              return {
                ...candidate,
                assignment: {
                  shiftApplicationId: variables.shiftApplicationId,
                  isLead: true,
                },
              };
            }
            return {
              ...candidate,
              assignment: candidate.assignment
                ? {
                    ...candidate.assignment,
                    isLead: false,
                  }
                : undefined,
            };
          })
      );

      return { previousCandidates };
    },
    onError(err, variables, context) {
      queryClient.setQueryData(
        ['turns', variables.id, 'candidates'],
        context?.previousCandidates
      );
    },
    async onSettled(data, error, variables) {
      queryClient.invalidateQueries({
        queryKey: ['turns', variables.id],
      });
    },
  });
};
